/**
 * Parse a localized amount string (e.g. "1.234,56" or "2,380€") into a JS number.
 *
 * The algorithm is **locale-agnostic** and relies on a few rules that hold for
 * most European / US formats:
 *   • Only one symbol is the decimal separator – it is always the **last**
 *     dot or comma in the string.
 *   • Every other dot/comma before that decimal mark is treated as a thousands
 *     separator and therefore removed.
 *   • If only one separator is present we decide whether it is decimal or
 *     thousands by looking at the number of digits that follow it. Up to
 *     `decimals` (default = 2, but we still allow 3 for currencies such as
 *     BHD) → treated as decimal; otherwise → thousands.
 *   • Everything that is not a digit, ".", "," or a minus sign is ignored.
 */
export function parseLocalizedNumber(value: string, decimals = 2): number {
  if (!value) return NaN;

  // Keep digits, dots, commas and minus sign only.
  const sanitized = value.replace(/[^0-9.,-]/g, '');

  if (sanitized === '' || sanitized === '.' || sanitized === ',' || sanitized === '-') {
    return NaN;
  }

  const lastDot = sanitized.lastIndexOf('.');
  const lastComma = sanitized.lastIndexOf(',');

  let decimalSeparator: '.' | ',' | null = null;

  // If both separators appear, choose the right-most one.
  if (lastDot !== -1 && lastComma !== -1) {
    decimalSeparator = lastDot > lastComma ? '.' : ',';
  } else if (lastDot !== -1) {
    const digitsAfterDot = sanitized.length - lastDot - 1;
    const startsWithZerosOrJustDot = /^-?0*\./.test(sanitized);
    if (
      digitsAfterDot > 0 &&
      (startsWithZerosOrJustDot || digitsAfterDot <= decimals || digitsAfterDot <= 2)
    ) {
      decimalSeparator = '.';
    }
  } else if (lastComma !== -1) {
    const digitsAfterComma = sanitized.length - lastComma - 1;
    const startsWithZerosOrJustComma = /^-?0*,/.test(sanitized);
    if (
      digitsAfterComma > 0 &&
      (startsWithZerosOrJustComma || digitsAfterComma <= decimals || digitsAfterComma <= 2)
    ) {
      decimalSeparator = ',';
    }
  }

  let numericString: string;

  if (decimalSeparator) {
    const [integerPart = '', fractionalPart = ''] = sanitized.split(decimalSeparator);
    const cleanedInteger = integerPart.replace(/[.,]/g, '');
    numericString = `${cleanedInteger}.${fractionalPart}`;
  } else {
    numericString = sanitized.replace(/[.,]/g, '');
  }

  const parsed = parseFloat(numericString);
  return Number.isNaN(parsed) ? NaN : parsed;
}
